import React, { useState, useRef } from "react";
import { Reveal, SectionMark, Shell, Glass, RegMarks } from "./Primitives";

const DETAILS = [
  {
    label: "Email",
    value: "gbengatimi166@gmail.com",
    href: "mailto:gbengatimi166@gmail.com",
  },
  { label: "Phone", value: "+234 704 346 4244", href: "tel:+2347043464244" },
  { label: "Location", value: "Lagos, Nigeria" },
  { label: "Timezone", value: "WAT / UTC+1" },
];

const Field = ({ label, id, as = "input", ...rest }) => {
  const Tag = as;
  return (
    <label htmlFor={id} className="block">
      <span className="label block pb-2.5">{label}</span>
      <Tag
        id={id}
        name={id}
        className={[
          "w-full bg-transparent border-b border-rule-2 pb-3 pt-1",
          "text-[15px] tracking-[-0.01em] text-ink placeholder:text-ink-25",
          "outline-none transition-colors duration-500 ease-glass",
          "focus:border-ink hover:border-ink-45",
          as === "textarea" ? "resize-none min-h-[104px]" : "",
        ].join(" ")}
        {...rest}
      />
    </label>
  );
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    number: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const [trap, setTrap] = useState(""); // honeypot - humans never fill this
  const openedAt = useRef(Date.now());

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          company: trap,
          elapsed: Date.now() - openedAt.current,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.error || "Could not send the message");
        setStatus("error");
        return;
      }

      setStatus("sent");
      setForm({ name: "", email: "", number: "", message: "" });
    } catch {
      setErrorMsg("Network error");
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36">
      <Shell>
        <SectionMark title="Contact" meta="Open to work" />

        <div className="grid gap-14 lg:grid-cols-[0.85fr_1fr] lg:gap-20 pt-10 md:pt-16">
          {/* Left - the statement and the particulars */}
          <div>
            <Reveal>
              <h2 className="display display-md">
                Let&rsquo;s build
                <br />
                something
                <br />
                <span className="text-ink-45">considered.</span>
              </h2>
            </Reveal>

            <Reveal delay={120}>
              <p className="lede pt-8 max-w-md">
                I take on a small number of projects at a time so each one gets
                real attention. Tell me what you&rsquo;re making.
              </p>
            </Reveal>

            <Reveal delay={200} className="pt-12">
              <dl className="border-t border-rule">
                {DETAILS.map((d) => (
                  <div
                    key={d.label}
                    className="flex items-baseline justify-between gap-6 border-b border-rule py-4"
                  >
                    <dt className="label">{d.label}</dt>
                    <dd className="text-[14px] tracking-[-0.01em] text-ink">
                      {d.href ? (
                        <a href={d.href} className="link-draw">
                          {d.value}
                        </a>
                      ) : (
                        d.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            <Reveal delay={280} className="flex items-center gap-3 pt-8">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-70" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
              </span>
              <span className="label text-ink-70">
                Available for new projects
              </span>
            </Reveal>
          </div>

          {/* Right - the glass pane */}
          <Reveal delay={160}>
            <Glass className="relative rounded-[28px] p-7 md:p-10">
              <RegMarks inset={14} />

              <form onSubmit={submit} className="space-y-8">
                <div className="grid gap-8 sm:grid-cols-2">
                  <Field
                    label="Name"
                    id="name"
                    value={form.name}
                    onChange={change}
                    required
                    placeholder="Your name"
                  />
                  <Field
                    label="Email"
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={change}
                    required
                    placeholder="you@studio.com"
                  />
                </div>

                <Field
                  label="Phone (optional)"
                  id="number"
                  type="tel"
                  value={form.number}
                  onChange={change}
                  placeholder="+234 000 000 0000"
                />

                {/* Honeypot. Hidden from people and from screen readers;
                    only scripted submitters ever populate it. */}
                <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={trap}
                    onChange={(e) => setTrap(e.target.value)}
                  />
                </div>

                <Field
                  label="Project"
                  id="message"
                  as="textarea"
                  rows="4"
                  value={form.message}
                  onChange={change}
                  required
                  placeholder="What are you building, and by when?"
                />

                <div className="flex flex-wrap items-center gap-5 pt-2">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className={[
                      "group inline-flex items-center gap-3 rounded-full bg-ink",
                      "px-7 py-3.5 text-[13.5px] font-medium tracking-[-0.005em]",
                      "text-ground-raise transition-opacity duration-500 ease-glass",
                      "hover:opacity-85 disabled:opacity-40",
                    ].join(" ")}
                  >
                    {status === "sending" ? "Sending" : "Send message"}
                    <span className="transition-transform duration-500 ease-glass group-hover:translate-x-1">
                      &rarr;
                    </span>
                  </button>

                  {status === "sent" && (
                    <span className="label text-signal">
                      Received. I&rsquo;ll reply shortly
                    </span>
                  )}
                  {status === "error" && (
                    <span className="label text-ink">
                      {errorMsg}. Email me directly at{" "}
                      <a href="mailto:gbengatimi166@gmail.com" className="underline">
                        gbengatimi166@gmail.com
                      </a>
                    </span>
                  )}
                </div>
              </form>
            </Glass>
          </Reveal>
        </div>
      </Shell>
    </section>
  );
};

export default Contact;
